import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, TextField, CircularProgress, Box, Typography, Stack, alpha,
} from '@mui/material';
import { Place, LocationOn } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { searchLocations, type LocationResult } from '../../services/locationService';
import { DISPATCH } from '../dispatcher/dispatchTheme';

interface LocationSearchFieldProps {
  label: string;
  placeholder?: string;
  value: LocationResult | null;
  onChange: (loc: LocationResult | null) => void;
  bias?: { lat: number; lng: number } | null;
  city?: string;
  accent?: string;
  required?: boolean;
  iconColor?: string;
  error?: boolean;
  helperText?: string;
  /** Allow long addresses to wrap instead of truncating with ellipsis. */
  multiline?: boolean;
  maxRows?: number;
}

function useDebouncedValue<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function LocationSearchField({
  label, placeholder = 'Search address, landmark, area…', value, onChange,
  bias, city, accent = DISPATCH.primary, required, iconColor = '#22c55e',
  error, helperText, multiline = false, maxRows = 3,
}: LocationSearchFieldProps) {
  const [input, setInput] = useState(value?.address || '');
  const debounced = useDebouncedValue(input, 350);

  useEffect(() => {
    if (value?.address && value.address !== input) setInput(value.address);
  }, [value?.address]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: options = [], isFetching } = useQuery({
    queryKey: ['location-search', debounced, bias?.lat, bias?.lng, city],
    queryFn: () => searchLocations(debounced, {
      lat: bias?.lat,
      lng: bias?.lng,
      city,
      limit: 8,
    }),
    enabled: debounced.trim().length >= 2 && debounced !== value?.address,
    staleTime: 30_000,
  });

  const mergedOptions = useMemo(() => {
    if (value && !options.some((o) => o.id === value.id)) return [value, ...options];
    return options;
  }, [options, value]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={mergedOptions}
      loading={isFetching}
      filterOptions={(x) => x}
      value={value}
      inputValue={input}
      onInputChange={(_, v, reason) => {
        setInput(v);
        if (reason === 'clear') onChange(null);
      }}
      onChange={(_, v) => {
        onChange(v);
        if (v) setInput(v.address);
      }}
      getOptionLabel={(o) => o.address || o.label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      noOptionsText={debounced.length < 2 ? 'Type at least 2 characters' : 'No locations found'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          multiline={multiline}
          maxRows={multiline ? maxRows : undefined}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Place fontSize="small" sx={{ color: iconColor, mr: 1, alignSelf: multiline ? 'flex-start' : 'center', mt: multiline ? 0.75 : 0 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              bgcolor: '#f8fafc',
              alignItems: multiline ? 'flex-start' : 'center',
              '& fieldset': { borderColor: 'rgba(15,23,42,0.08)' },
              '&:hover': { bgcolor: '#fff', '& fieldset': { borderColor: alpha(accent, 0.4) } },
              '&.Mui-focused': {
                bgcolor: '#fff',
                boxShadow: `0 0 0 3px ${alpha(accent, 0.14)}`,
                '& fieldset': { borderColor: accent, borderWidth: 1.5 },
              },
            },
            ...(multiline
              ? { '& .MuiInputBase-input': { whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'visible', textOverflow: 'clip' } }
              : {}),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id} sx={{ py: 1.25, px: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={{
              mt: 0.25, width: 28, height: 28, borderRadius: 1.5, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(accent, 0.1), color: accent,
            }}>
              <LocationOn sx={{ fontSize: 16 }} />
            </Box>
            <Box minWidth={0}>
              <Typography variant="body2" fontWeight={700} noWrap>{option.label}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {option.address}
              </Typography>
            </Box>
          </Stack>
        </Box>
      )}
      slotProps={{
        paper: {
          elevation: 8,
          sx: { mt: 0.5, borderRadius: 2.5, border: `1px solid ${DISPATCH.border}`, boxShadow: DISPATCH.shadowLg },
        },
      }}
    />
  );
}
